'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface TimeSlot {
  time: string;
  available: boolean;
}

const ConsultationWidget = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const timeSlots: TimeSlot[] = [
    { time: '9:00 AM', available: true },
    { time: '10:30 AM', available: true },
    { time: '1:00 PM', available: false },
    { time: '2:30 PM', available: true },
    { time: '4:00 PM', available: true }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-accent/5 to-brand-purple/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full">
              <Icon name="CalendarIcon" size={20} className="text-accent" />
              <span className="text-sm font-medium text-accent">Free Strategy Session</span>
            </div>

            <h2 className="text-3xl lg:text-5xl font-display font-bold text-foreground">
              Ready to Transform Your Business?
            </h2>

            <p className="text-lg text-muted-foreground leading-relaxed">
              Book a complimentary 45-minute consultation with our AI automation experts. We'll analyze your current operations, identify opportunities, and create a customized roadmap for your digital transformation.
            </p>

            <div className="space-y-4">
              {[
                'Personalized AI automation strategy',
                'ROI projections and timeline',
                'Technology stack recommendations',
                'Implementation roadmap',
                'No obligation, just insights'
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-success/10 flex items-center justify-center mt-0.5">
                    <Icon name="CheckIcon" size={16} className="text-success" />
                  </div>
                  <span className="text-foreground">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center space-x-6 pt-4">
              <div className="flex -space-x-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="w-10 h-10 rounded-full border-2 border-background bg-muted"
                  ></div>
                ))}
              </div>
              <div className="text-sm">
                <div className="font-semibold text-foreground">Join 500+ businesses</div>
                <div className="text-muted-foreground">Already transformed with CIS Solutions</div>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-2xl p-8 shadow-elevation border border-border">
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-border">
                <h3 className="text-xl font-display font-bold text-foreground">
                  Request Your Quote
                </h3>
                <div className="flex items-center space-x-1 text-success text-sm font-medium">
                  <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                  <span>Available Now</span>
                </div>
              </div>

              {isHydrated && (
                <>
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-foreground">
                      Select Date
                    </label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </div>

                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-foreground">
                      Available Time Slots
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {timeSlots.map((slot, index) => (
                        slot.available ? (
                          <Link
                            key={index}
                            href="/contact"
                            className="px-4 py-3 rounded-lg text-sm font-medium transition-all duration-300 bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground border border-primary/20 text-center"
                          >
                            {slot.time}
                          </Link>
                        ) : (
                          <button
                            key={index}
                            disabled
                            className="px-4 py-3 rounded-lg text-sm font-medium bg-muted text-muted-foreground cursor-not-allowed border border-border"
                          >
                            {slot.time}
                          </button>
                        )
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="pt-4 border-t border-border">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-display font-bold text-accent">45</div>
                    <div className="text-xs text-muted-foreground mt-1">Minutes</div>
                  </div>
                  <div>
                    <div className="text-2xl font-display font-bold text-success">Free</div>
                    <div className="text-xs text-muted-foreground mt-1">No Cost</div>
                  </div>
                  <div>
                    <div className="text-2xl font-display font-bold text-brand-purple">24h</div>
                    <div className="text-xs text-muted-foreground mt-1">Response</div>
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                By submitting, you agree to our terms of service and privacy policy
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConsultationWidget;